import React, { createContext, useContext, useEffect, useState } from 'react'
import { ThemeContextType } from './ThemeProvider.types'
import {
  DEFAULT_THEME,
  getThemeFromStorage,
  setThemeToStorage,
  THEME_STORAGE_EVENT,
  ThemeStorageEventDetail,
  updateDocumentBodyByTheme,
} from '../utils/theme-utils'
import { Theme } from '../constants/theme'

// Event Effects
import SnowOverlay from '../components/EventEffects/SnowOverlay'
import PetalOverlay from '../components/EventEffects/PetalOverlay'
import LeafOverlay from '../components/EventEffects/LeafOverlay'

// Extend Window interface for theme switching
declare global {
  interface Window {
    switchTheme?: (theme: string) => void
  }
}

// Pre-import all theme CSS files
// Base theme
import '../assets/themes/base.css'
// Gold themes
import '../assets/themes/gold/css/light/styles.css'
import '../assets/themes/gold/css/dark/styles.css'
// Blue themes
import '../assets/themes/blue/css/light/styles.css'
import '../assets/themes/blue/css/dark/styles.css'

// Event effect CSS
import '../assets/events/christmas/christmas-effects.css'
import '../assets/events/songkran-festival/songkran-festival.css'
import '../assets/events/halloween/halloween.css'
import '../assets/events/autumn-leaves/autumn-leaves.css'
import '../assets/events/sakura/sakura.css'
import '../assets/events/chinese-new-year/chinese-new-year-effects.css'
import '../assets/events/loy-krathong/loy-krathong.css'

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeProviderProps {
  children: React.ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = getThemeFromStorage()
    return saved || DEFAULT_THEME
  })

  useEffect(() => {
    updateDocumentBodyByTheme(theme)
    setThemeToStorage(theme)
  }, [theme])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleThemeStorageChange = (event: Event) => {
      const { detail } = event as CustomEvent<ThemeStorageEventDetail>
      const nextTheme = detail?.theme

      if (!nextTheme || !Object.values(Theme).includes(nextTheme)) {
        return
      }

      setTheme((current) => (current === nextTheme ? current : nextTheme))
    }

    window.addEventListener(THEME_STORAGE_EVENT, handleThemeStorageChange)

    return () => {
      window.removeEventListener(THEME_STORAGE_EVENT, handleThemeStorageChange)
    }
  }, [])

  // Expose theme switching to window object for console access
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.switchTheme = (newTheme: string) => {
        if (Object.values(Theme).includes(newTheme as Theme)) {
          setTheme(newTheme as Theme)
        }
      }

      return () => {
        if (typeof window !== 'undefined') {
          delete window.switchTheme
        }
      }
    }
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <SnowOverlay />
      <PetalOverlay />
      <LeafOverlay />
      {children}
    </ThemeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
